import { ArrowUpIcon } from "@phosphor-icons/react/dist/csr/ArrowUp";
import { useLayoutEffect } from "react";
import type {
  FormEvent,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
} from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ShortcutKbd } from "@volca/ui/components/ui/shortcut-kbd";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

const MAX_TEXTAREA_HEIGHT_PX = 160;

interface HarnessChatInputProps {
  draft: string;
  disabled: boolean;
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  header?: ReactNode;
  /** Beside the send button, such as a way to add a screenshot. */
  actions?: ReactNode;
  textareaClassName?: string;
}

export const HarnessChatInput = ({
  draft,
  disabled,
  textareaRef,
  onDraftChange,
  onSend,
  onKeyDown,
  placeholder = "Ask anything about this payroll setup...",
  header,
  actions,
  textareaClassName,
}: HarnessChatInputProps) => {
  const canSend = draft.trim().length > 0 && !disabled;

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [draft, textareaRef]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canSend) onSend();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key !== "Enter" || event.shiftKey) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) onSend();
  };

  return (
    <div className="shrink-0 p-3">
      {header}
      <form
        onSubmit={handleSubmit}
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest("button, textarea, input, a")) return;
          event.preventDefault();
          textareaRef.current?.focus();
        }}
        className="flex cursor-text flex-col rounded-2xl border border-line bg-surface-raised shadow-raised focus-within:ring-2 focus-within:ring-focus"
      >
        {/* Not InputGroup: it dims itself whenever anything inside is disabled, and the
            send button is disabled whenever the draft is empty. */}
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Message"
          rows={1}
          disabled={disabled}
          className={cn(
            "max-h-40 min-h-11 w-full resize-none bg-transparent px-3 pt-3 text-base text-ink placeholder:text-subtle focus:outline-hidden disabled:opacity-50 md:text-sm",
            textareaClassName,
          )}
        />
        <Stack
          direction="row"
          align="center"
          justify={actions ? "between" : "end"}
          className="px-2 pb-2"
        >
          {actions}
          <Button
            type="submit"
            size="icon"
            disabled={!canSend}
            aria-label="Send"
          >
            <ArrowUpIcon />
          </Button>
        </Stack>
      </form>
      <p className="mt-2 flex items-center justify-center gap-1 text-xs text-subtle">
        <ShortcutKbd keys={["Enter"]} /> send ·{" "}
        <ShortcutKbd keys={["shift", "Enter"]} /> newline
      </p>
    </div>
  );
};
